import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, IconButton, List, ListItem, ListItemText, Paper, Stack, Tooltip, Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RiceBowlIcon from '@mui/icons-material/RiceBowl';
import ShuffleIcon from '@mui/icons-material/Shuffle';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router-dom';
import * as api from '../plannerApi';
import * as T from '../types';
import { EmptyState, NeedsHousehold, useNotify } from '../common';
import { dayLabel, mealText, sidesText, weekTitle } from '../labels';
import { SideChooserDialog, asSide, planUrl, useWeekParam } from './shared';

function SuggestContent() {
  const navigate = useNavigate();
  const notify = useNotify();
  const week = useWeekParam();
  const [slots, setSlots] = useState<T.MealSlot[]>([]);
  const [draft, setDraft] = useState<T.SuggestedMeal[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState<T.SuggestedMeal | null>(null);
  const [sideRecipes, setSideRecipes] = useState<T.RecipeSide[] | null>(null);
  const drawId = useRef(0);

  const draw = useCallback(async () => {
    const id = ++drawId.current;
    setBusy(true);
    setError(null);
    try {
      // Sequential: both calls create the week on first access
      const plan = await api.getWeekPlan(week);
      const meals = await api.suggestWeek(week);
      if (id !== drawId.current) return;
      setSlots(plan.slots);
      setDraft(meals);
    } catch (e: any) {
      if (id === drawId.current) setError(e.message ?? 'Ocorreu um erro.');
    } finally {
      if (id === drawId.current) setBusy(false);
    }
  }, [week]);

  useEffect(() => { draw(); }, [draw]);

  const describe = (slotId: T.Guid) => {
    const slot = slots.find(s => s.publicId === slotId);
    return slot ? `${dayLabel(slot.date)} · ${mealText(slot.mealType)}` : '';
  };

  const replace = (old: T.SuggestedMeal, meal: T.SuggestedMeal) =>
    setDraft(list => (list ?? []).map(d => (d === old ? meal : d)));

  /** Swaps one meal for another of the best-ranked recipes not already in the draft. */
  const reroll = async (meal: T.SuggestedMeal) => {
    try {
      const ranking = await api.getSlotSuggestions(meal.slotPublicId, false, 10);
      const taken = (draft ?? []).map(d => d.recipePublicId);
      const options = ranking.filter(r => !taken.includes(r.recipePublicId)).slice(0, 3);
      if (options.length === 0) {
        notify('Sem alternativas: não há outra receita que encaixe nesta refeição.', { severity: 'info' });
        return;
      }
      const pick = options[Math.floor(Math.random() * options.length)];
      replace(meal, {
        slotPublicId: meal.slotPublicId,
        recipePublicId: pick.recipePublicId,
        recipeName: pick.name,
        sides: pick.suggestedSide ? [pick.suggestedSide] : [],
        usualSides: pick.usualSides,
      });
    } catch (e: any) {
      setError(e.message ?? 'Ocorreu um erro.');
    }
  };

  /** Changes the sides of one meal (its usual sides first, then the other side dishes). */
  const chooseSides = async (meal: T.SuggestedMeal) => {
    if (!sideRecipes) {
      try {
        setSideRecipes((await api.getRecipes()).filter(r => r.course === 'Side').map(asSide));
      } catch (e: any) {
        setError(e.message ?? 'Ocorreu um erro.');
        return;
      }
    }
    setEditing(meal);
  };

  const remove = (meal: T.SuggestedMeal) => {
    const index = (draft ?? []).indexOf(meal);
    setDraft(list => (list ?? []).filter(d => d !== meal));
    notify(`${meal.recipeName} saiu do rascunho.`, {
      action: () => setDraft(list => {
        const next = (list ?? []).slice();
        next.splice(Math.min(index, next.length), 0, meal);
        return next;
      }),
    });
  };

  const apply = async () => {
    if (!draft || draft.length === 0) {
      navigate(planUrl(week));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.applySuggestions(week, draft.map(d => ({
        slotPublicId: d.slotPublicId, recipePublicId: d.recipePublicId, sidePublicIds: d.sides.map(s => s.publicId),
      })));
      navigate(planUrl(week));
    } catch (e: any) {
      setError(e.message ?? 'Ocorreu um erro.');
      setBusy(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 720 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
        <IconButton aria-label="Voltar" edge="start" onClick={() => navigate(planUrl(week))}><ArrowBackIcon /></IconButton>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h6" component="h1" sx={{ fontWeight: 600 }}>Sugestão da semana</Typography>
          <Typography variant="body2" color="text.secondary">Semana de {weekTitle(week)}</Typography>
        </Box>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Rascunho para as refeições por definir. Muda o acompanhamento, troca o prato ou deixa a refeição como está. Nada é guardado até aplicares.
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 1.5, whiteSpace: 'pre-line' }}>{error}</Alert>}

      {draft === null ? (
        busy && <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : draft.length === 0 ? (
        <EmptyState title="Nada para sugerir" text="Não há refeições por definir nesta semana (ou estão bloqueadas)." />
      ) : (
        <Paper variant="outlined" sx={{ opacity: busy ? 0.6 : 1 }}>
          <List disablePadding>
            {draft.map((meal, i) => (
              <ListItem
                key={`${meal.slotPublicId}-${meal.recipePublicId}`}
                divider={i < draft.length - 1}
                sx={{ pr: 1, alignItems: 'center' }}
              >
                <ListItemText
                  sx={{ minWidth: 0, mr: 1 }}
                  primary={<Typography variant="caption" color="text.secondary">{describe(meal.slotPublicId)}</Typography>}
                  secondary={<Typography variant="body1" color="text.primary" sx={{ overflowWrap: 'anywhere' }}>{meal.recipeName}{sidesText(meal.sides)}</Typography>}
                  disableTypography
                />
                <Stack direction="row" sx={{ flexShrink: 0 }}>
                  <Tooltip title="Mudar o acompanhamento">
                    <IconButton aria-label="Mudar o acompanhamento" onClick={() => chooseSides(meal)} disabled={busy}><RiceBowlIcon /></IconButton>
                  </Tooltip>
                  <Tooltip title="Trocar o prato">
                    <IconButton aria-label="Trocar o prato" onClick={() => reroll(meal)} disabled={busy}><ShuffleIcon /></IconButton>
                  </Tooltip>
                  <Tooltip title="Deixar como está">
                    <IconButton aria-label="Deixar como está" onClick={() => remove(meal)} disabled={busy}><CloseIcon /></IconButton>
                  </Tooltip>
                </Stack>
              </ListItem>
            ))}
          </List>
        </Paper>
      )}

      {editing && sideRecipes && (
        <SideChooserDialog
          open
          current={editing.sides}
          options={editing.usualSides.concat(sideRecipes.filter(s => editing.usualSides.every(u => u.publicId !== s.publicId)))}
          onClose={chosen => {
            if (chosen) replace(editing, { ...editing, sides: chosen });
            setEditing(null);
          }}
        />
      )}

      <Stack direction="row" spacing={1.5} sx={{ position: 'sticky', bottom: { xs: 64, md: 0 }, py: 1.5, mt: 2, bgcolor: 'background.default', zIndex: 1 }}>
        <Button fullWidth variant="outlined" onClick={draw} disabled={busy}>Sortear de novo</Button>
        <Button fullWidth variant="contained" onClick={apply} disabled={busy}>Aplicar</Button>
      </Stack>
    </Box>
  );
}

/** Draft of the week's meals from the suggestion engine; applied only on demand. */
const SuggestWeekPage: React.FC = () => <NeedsHousehold><SuggestContent /></NeedsHousehold>;

export default SuggestWeekPage;
